import React, { useRef } from 'react';
import { Button, Card, Col, QRCode, Row, Space, Spin } from 'antd';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { observer } from 'mobx-react-lite';
import { trackPromise } from 'react-promise-tracker';
import { ColumnFilter } from 'common/components/table/filters/column-filter/column-filter';
import { ColumnVisibility } from 'common/components/table/filters/column-visibility/column-visibility';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import TableBase from 'common/components/table/table-base';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import {
  filterColumnsByPersonalization,
  getTablePersonalizationColumnsData,
  getTablePersonalizationSortData,
  personalizationDefaultTaleSort,
} from 'common/components/table/table-helpers';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { downloadCsv } from 'common/helpers/downloadCsv.helper';
import citationService from 'common/services/citation-service/citation.service';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import exportService from 'common/services/export-service/export.service';
import configurationStore from 'common/stores/configuration/configuration.store';
import userPersonalizationStore from 'common/stores/user-personalization/user-personalization.store';
import { notification } from 'common/utils/notification';
import { CitationStatus } from 'features/eiq-manage/enums/citation-status.enum';
import CitationActionButtons from './citation-actions/citation-action-buttons';
import { citationFilterConfig, citationsInitialColumns } from './citations.config';
import EditCitationModal from './edit-citation-modal/edit-citation-modal';
import { getCitationReportDateRange, hasActivePenalty } from './helpers/citation-utils';
import { useCitationModals } from './hooks/use-citation-modals';
import MoveCitationModal from './move-citation-modal/move-citation-modal';
import PrintCitationList from './print-citation-list/print-citation-list';
import { PrintCitationDetails } from './print-citation-details/print-citation-details';
import { IPrintCitaitonDetailsData } from './print-citation-details/print-citation-details.types';
import { getFile } from 'common/helpers/image.helper';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import { getLegalNotes, printPdf } from './helpers/citations-helper';
import './citations.scss';
import featureStore from 'common/stores/features/feature.store';

const TABLE_NAME = 'citations';
const EXPORT_PAGE_LIMIT = 10_000;

export const Citations = observer(() => {
  const tableControls = useTableControls({
    onFetch: citationService.getCitations,
    initialColumns: filterColumnsByPersonalization(citationsInitialColumns, TABLE_NAME),
    defaultSort: personalizationDefaultTaleSort(TABLE_NAME),
    onUpdateColumns: async (columns: any, sortBy: any) => {
      await userPersonalizationStore.updateTableColumnsPersonalization({
        tableName: TABLE_NAME,
        tableColumns: getTablePersonalizationColumnsData(columns),
        tableSort: getTablePersonalizationSortData(sortBy),
      });
    },
  });

  const selectedCitation = tableControls.getSelectedRow() as ICitationRead | undefined;
  const { moveCitationModal, editCitationModal } = useCitationModals(selectedCitation, () => tableControls.refetchData());
  const citationConfiguration = configurationStore.configuration?.citations;

  const citationPaymentLinkQR = useRef<HTMLDivElement>(null);

  const getQrImage = () => {
    if (!tableControls.getSelectedRow()?.paymentLinkUrl) {
      return null;
    }

    const canvas = citationPaymentLinkQR.current?.querySelector<HTMLCanvasElement>('canvas');
    return canvas?.toDataURL('image/png');
  };

  const buildPrintCitationDetailsPdfProps = async (row: ICitationRead): Promise<IPrintCitaitonDetailsData> => {
    const imagesUrls = [...(row.photosUrl ?? []), ...(row.licensePlatePhotosUrl ?? [])];

    const evidenceImagesSrc = await Promise.all(imagesUrls.map((url) => getFile(url, UploadFileTypes.Citation)));
    const paymentLinkQRSrc = getQrImage();

    return {
      ...row,
      evidenceImagesSrc,
      paymentLinkQRSrc,
    } as IPrintCitaitonDetailsData;
  };

  const getTableQuery = () => ({
    pageLimit: { page: 1, limit: EXPORT_PAGE_LIMIT },
    tableFilters: tableControls.getFilters(),
    tableSortBy: tableControls.getSortBy(),
  });

  const updateSelectedCitationStatus = async (
    action: (citationId: number, residentId: number) => Promise<any>,
    nextStatus: CitationStatus,
  ): Promise<void> => {
    if (!selectedCitation) {
      return;
    }

    const citationToUpdate = selectedCitation;
    await action(citationToUpdate.id, citationToUpdate.residentId!);

    const currentSelection = tableControls.getSelectedRow() as ICitationRead | undefined;
    if (currentSelection?.id === citationToUpdate.id) {
      tableControls.updateSelectedRow({ ...citationToUpdate, status: nextStatus });
    }
  };

  const handleConfirmPayment = () =>
    updateSelectedCitationStatus((citationId, residentId) => citationService.confirmPayment(citationId, residentId), CitationStatus.Paid);

  const handleApprove = () =>
    updateSelectedCitationStatus((citationId, residentId) => citationService.approve(citationId, residentId), CitationStatus.PayNow);
  const handleWaive = () =>
    updateSelectedCitationStatus((citationId, residentId) => citationService.waive(citationId, residentId), CitationStatus.Waived);
  const handleConfirm = () =>
    updateSelectedCitationStatus((citationId, residentId) => citationService.confirm(citationId, residentId), CitationStatus.Confirmed);

  const handleCancel = async () => {
    if (!selectedCitation) {
      return;
    }

    const shouldShowSuspensionMessage = hasActivePenalty(selectedCitation.status);
    await updateSelectedCitationStatus((citationId, residentId) => citationService.cancel(citationId, residentId), CitationStatus.Canceled);

    if (shouldShowSuspensionMessage) {
      notification.success({ message: 'Citation and the related suspension were cancelled.', duration: 5 });
    }
  };

  const handlePrintCitationDetails = async () => {
    await trackPromise(
      (async () => {
        const citation = tableControls.getSelectedRow() as ICitationRead;
        const logo = await configurationStore.getCommunityLogoForPDF();
        const data = await buildPrintCitationDetailsPdfProps(citation);
        const communityName = configurationStore.configuration?.default.communityName || '';
        const isAutoIssueInvoice = citationConfiguration?.isAutoIssueInvoice || false;
        const generalInfo = citationConfiguration?.generalInformation;
        const legalNotes = getLegalNotes(citationConfiguration?.rules ?? [], citation.sequenceNumber || 1);

        const report = (
          <PrintCitationDetails
            communityLogo={logo}
            data={data}
            communityName={communityName}
            isAutoIssueInvoice={isAutoIssueInvoice}
            citationGeneralInfo={generalInfo}
            legalNotes={legalNotes}
          />
        );
        const blob = await pdf(report).toBlob();

        printPdf(blob);
      })(),
    );
  };

  const handleExportCitationListPdf = async () => {
    const { dateFrom, dateTo } = getCitationReportDateRange(tableControls.getFilters());

    await trackPromise(
      (async () => {
        const { pageLimit, tableFilters, tableSortBy } = getTableQuery();
        const data = await citationService.getCitations(pageLimit, tableFilters, tableSortBy);
        const logo = await configurationStore.getCommunityLogoOrDefaultForPDF();
        const report = (
          <PrintCitationList
            citations={data.items}
            dateFrom={dateFrom.format('MM/DD/YYYY')}
            dateTo={dateTo.format('MM/DD/YYYY')}
            logo={logo}
          />
        );
        const blob = await pdf(report).toBlob();
        const fileName = `IntelliTicket-Violations_${dateFrom.format('MM-DD-YYYY')}_${dateTo.format('MM-DD-YYYY')}.pdf`;
        saveAs(blob, fileName);
      })(),
    );
  };

  const handleExportCsv = async () => {
    const { pageLimit, tableFilters, tableSortBy } = getTableQuery();
    const blob = await exportService.processCitations(pageLimit, tableFilters, tableSortBy);
    downloadCsv(blob, 'citations.csv');
  };

  const getRowClassName = (_rowIndex: number, rowData: ICitationRead): string => {
    return rowData.residentId ? '' : 'missingTrafficLogRow';
  };

  return (
    <>
      {tableControls.getSelectedRow()?.paymentLinkUrl && (
        <div ref={citationPaymentLinkQR} style={{ position: 'absolute', left: -9999 }}>
          <QRCode value={String(tableControls.getSelectedRow()?.paymentLinkUrl)} type="canvas" size={200} />
        </div>
      )}

      <TableContextProvider controls={tableControls}>
        <Card className="eiq-card eiq-card-border full-card">
          <Row className="eiq-card-header">
            <Col span={24}>
              <h1 className="h1">Citations</h1>
            </Col>
          </Row>

          <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
            <Col flex="auto">
              <SearchFilter
                placeholder="Search by resident name, license plate, guest name, citation #"
                rulesForColumn={{
                  vehicle_licensePlate: FilterTypesEnum.Contains,
                  owner_guestName: FilterTypesEnum.Contains,
                  owner_residentName: FilterTypesEnum.Contains,
                  externalId: FilterTypesEnum.Contains,
                }}
              />
            </Col>

            <Col>
              <Space size={8} wrap>
                <Button type="primary" size="middle" onClick={handleExportCsv} className="mobile-btn">
                  Export CSV
                </Button>
                <Button type="primary" size="middle" onClick={handleExportCitationListPdf} className="mobile-btn">
                  Export PDF
                </Button>
                <CitationActionButtons
                  citation={selectedCitation}
                  isConfirmPaymentEnabled={featureStore.isEIQIntelliTicketConfirmPaymentEnabled}
                  onPrint={handlePrintCitationDetails}
                />
                <ColumnVisibility isColumnReorderAllow />
                <ColumnFilter filterConfig={citationFilterConfig} />
              </Space>
            </Col>
          </Row>
          <TableBase onSelected={tableControls.setSelectedRow} selectFirstRowByDefault rowClassName={getRowClassName} />
        </Card>
      </TableContextProvider>

      <MoveCitationModal
        initData={moveCitationModal.initData}
        title="Move citation"
        isOpen={moveCitationModal.isOpenModal}
        onClose={moveCitationModal.hideModal}
      />
      <EditCitationModal
        initData={editCitationModal.initData}
        title="Edit License Plate Number and Type"
        isOpen={editCitationModal.isOpenModal}
        onClose={editCitationModal.hideModal}
      />
    </>
  );
});
